import React, { createContext, useContext, useEffect, useState } from "react";
import { clientService } from "@/services/api/clientService";
import { toast } from "react-toastify";
const ClientContext = createContext();

export const useClient = () => {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error('useClient must be used within a ClientProvider');
  }
  return context;
};
export const ClientProvider = ({ children }) => {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClientState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [facebookMetadata, setFacebookMetadata] = useState({});
  const [historicalData, setHistoricalData] = useState([]);

  // Wrapper function that saves to localStorage when client changes
  const setSelectedClient = (client) => {
    setSelectedClientState(client);
    if (client) {
      localStorage.setItem('selectedClientId', client.Id.toString());
      console.log('CLIENT_CONTEXT_DEBUG: Saved client to localStorage:', client.Id);
    } else {
      localStorage.removeItem('selectedClientId');
      console.log('CLIENT_CONTEXT_DEBUG: Removed client from localStorage');
    }
  };

  useEffect(() => {
    loadClients().then((loaded) => {
      // If first attempt returned no clients, retry once after a short delay
      // to allow the Apper SDK session to fully settle after ApperUI.setup()
      if (!loaded) {
        setTimeout(() => loadClients(), 2000);
      }
    });
  }, []);

const loadClients = async () => {
    try {
      console.log('CLIENT_CONTEXT_DEBUG: Starting client data loading');
      setLoading(true);

      const clientsData = await clientService.getAll();
      console.log('CLIENT_CONTEXT_DEBUG: Service returned data:', {
        isArray: Array.isArray(clientsData),
        count: clientsData?.length || 0,
        type: typeof clientsData,
        clients: Array.isArray(clientsData) ? clientsData.map(c => ({
          id: c.Id,
          name: c.companyName,
          hasName: !!c.companyName
        })) : 'Not an array'
      });

      // Ensure we have an array and set the state
      const clients = Array.isArray(clientsData) ? clientsData : [];
      console.log('CLIENT_CONTEXT_DEBUG: Setting clients state with', clients.length, 'clients');
      setClients(clients);
      if (clients.length === 0) return false;
      
      // Try to restore previously selected client from localStorage
      const savedClientId = localStorage.getItem('selectedClientId');
      let clientToSelect = null;
      
      if (savedClientId && clients.length > 0) {
        const savedClient = clients.find(c => c.Id === parseInt(savedClientId));
        if (savedClient) {
          clientToSelect = savedClient;
          console.log('CLIENT_CONTEXT_DEBUG: Restored client from localStorage:', savedClient.companyName);
        }
      }
      
      // Auto-select first client if none selected and clients exist
      if (!clientToSelect && !selectedClient && clients && clients.length > 0) {
        clientToSelect = clients[0];
        console.log('CLIENT_CONTEXT_DEBUG: Auto-selected first client:', clients[0]?.companyName);
      }
      
      if (clientToSelect) {
        setSelectedClient(clientToSelect);
      } else if (clients.length === 0) {
        console.log('CLIENT_CONTEXT_DEBUG: No clients to auto-select');
        setSelectedClient(null);
      }
      
      // Load Facebook metadata and historical data
      loadFacebookMetadata();
      loadHistoricalData();
      
      console.log('CLIENT_CONTEXT_DEBUG: Client initialization completed successfully with', clients.length, 'clients');
      return true;
    } catch (error) {
      console.error('CLIENT_CONTEXT_ERROR: Failed to load clients:', {
        message: error.message,
        stack: error.stack,
        error: error
      });
      // Ensure we still set an empty array to prevent undefined state
      setClients([]);
      setSelectedClient(null);
      return false;
    } finally {
      console.log('CLIENT_CONTEXT_DEBUG: Setting client loading to false');
      setLoading(false);
    }
  };

  // Filter clients based on search term
  const filteredClients = clients.filter(client => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return client.companyName?.toLowerCase().includes(searchLower) ||
           client.clientInformation?.toLowerCase().includes(searchLower) ||
           client.websiteUrl?.toLowerCase().includes(searchLower) ||
           client.brandVoice?.profileSummary?.toLowerCase().includes(searchLower) ||
           client.brandVoice?.keywords?.toLowerCase().includes(searchLower) ||
           client.brandVoice?.voice?.toLowerCase().includes(searchLower) ||
client.brandVoice?.targetAudience?.toLowerCase().includes(searchLower) ||
           client.brandVoice?.description?.toLowerCase().includes(searchLower) ||
           client.brandVoice?.benefits?.toLowerCase().includes(searchLower) ||
           client.brandVoice?.campaignObjective?.toLowerCase().includes(searchLower) ||
           client.brandVoice?.offerCategory?.toLowerCase().includes(searchLower) ||
           client.brandVoice?.brandToneVoice?.toLowerCase().includes(searchLower) ||
           client.brandVoice?.topCustomerDesires?.toLowerCase().includes(searchLower) ||
           client.brandVoice?.primaryCustomerProblems?.toLowerCase().includes(searchLower) ||
           client.brandVoice?.proofAssets?.toLowerCase().includes(searchLower) ||
           client.brandVoice?.audiences?.some(
             aud =>
               aud?.name?.toLowerCase().includes(searchLower) ||
               aud?.description?.toLowerCase().includes(searchLower)
           );
  });
const addClient = async (clientData) => {
    try {
      console.log('CLIENT_CONTEXT_DEBUG: Adding client with data:', clientData);
      const newClient = await clientService.create(clientData);
      console.log('CLIENT_CONTEXT_DEBUG: Client created successfully:', newClient);
      
      // Update clients list with new client at the beginning
      setClients(prev => {
        const updatedClients = [newClient, ...prev];
        console.log('CLIENT_CONTEXT_DEBUG: Updated clients list:', { 
          previousCount: prev.length, 
          newCount: updatedClients.length,
          newClientId: newClient.Id 
        });
        return updatedClients;
      });
      
      // Auto-select newly created client
      setSelectedClient(newClient);
      console.log('CLIENT_CONTEXT_DEBUG: Selected new client:', newClient.companyName);
      
      return newClient;
    } catch (error) {
      console.error('CLIENT_CONTEXT_ERROR: Failed to add client:', error);
      throw error;
    }
  };

const updateClient = async (clientId, clientData) => {
    try {
      const updatedClient = await clientService.update(clientId, clientData);
      setClients(prev => prev.map(client =>
        client.Id === updatedClient.Id ? updatedClient : client
      ));
      if (selectedClient?.Id === updatedClient.Id) {
        setSelectedClient(updatedClient);
      }
      return updatedClient;
    } catch (error) {
      throw error;
    }
  };

  const deleteClient = async (clientId) => {
    try {
      await clientService.delete(clientId);
      setClients(prev => prev.filter(client => client.Id !== clientId));
      if (selectedClient?.Id === clientId) {
        const remainingClients = clients.filter(client => client.Id !== clientId);
        setSelectedClient(remainingClients.length > 0 ? remainingClients[0] : null);
      }
    } catch (error) {
      throw error;
    }
};

  const loadFacebookMetadata = () => {
    const stored = localStorage.getItem('facebook_metadata');
    if (stored) {
      setFacebookMetadata(JSON.parse(stored));
    }
  };

  const loadHistoricalData = () => {
    const stored = localStorage.getItem('historical_data');
    if (stored) {
      setHistoricalData(JSON.parse(stored));
    }
  };

  const saveFacebookMetadata = (metadata) => {
    setFacebookMetadata(prev => ({ ...prev, ...metadata }));
    localStorage.setItem('facebook_metadata', JSON.stringify({ ...facebookMetadata, ...metadata }));
  };

  const saveHistoricalData = (data) => {
    const updatedHistory = [...historicalData, { ...data, timestamp: new Date().toISOString() }];
    setHistoricalData(updatedHistory);
    localStorage.setItem('historical_data', JSON.stringify(updatedHistory));
  };

  const recoverHistoricalData = (id) => {
    return historicalData.find(item => item.id === id);
  };

const value = {
    clients,
    filteredClients,
    selectedClient,
    setSelectedClient,
    loading,
    searchTerm,
    setSearchTerm,
    loadClients,
    addClient,
    updateClient,
    deleteClient,
    facebookMetadata,
    historicalData,
    saveFacebookMetadata,
    saveHistoricalData,
    recoverHistoricalData
  };

  return (
    <ClientContext.Provider value={value}>
      {children}
    </ClientContext.Provider>
  );
};